import { initLucideIcons } from './lucide-icons';

function createErrorMsgElement(errorText: string): HTMLDivElement {
    const messageWrapper = document.createElement('div');
    const icon = document.createElement('i');

    icon.setAttribute('data-lucide', 'circle-x');
    icon.setAttribute('aria-hidden', 'true');
    messageWrapper.classList.add('text-danger');
    messageWrapper.appendChild(icon);
    messageWrapper.appendChild(document.createTextNode(` ${errorText}`));
    return messageWrapper;
}

function getSelect2DropdownParent($el: JQuery): JQuery {
    const $modal = $el.closest('.modal');
    if ($modal.length) {
        return $modal;
    }

    return $(document.body);
}

function initEntityItemSelects($row: JQuery): void {
    const $ = window.jQuery;
    if (!$?.fn?.select2) {
        return;
    }

    $row.find('select.entityItemInput:not(.select2-ignore):not(:disabled)').each(function eachEntityItemSelect() {
        const $select = $(this);
        if ($select.data('select2')) {
            return;
        }

        $select.select2({
            width: '100%',
            dropdownParent: getSelect2DropdownParent($select),
        });
    });
}

function initRowWidgets($row: JQuery): void {
    $row.find('input:not([type="hidden"]), textarea, select').addClass('form-control');

    if (typeof window.init_form_selects === 'function') {
        window.init_form_selects($row[0] as HTMLElement);
    }
    initEntityItemSelects($row);
    if (typeof window.initNativeChecks === 'function') {
        window.initNativeChecks($row[0] as HTMLElement);
    }
    if (typeof window.initNetteForms === 'function') {
        window.initNetteForms($row[0] as HTMLElement);
    }
    if (typeof window.initFileInputUploads === 'function') {
        window.initFileInputUploads($row[0] as HTMLElement);
    }
    if (typeof window.initEntitiesAutocomplete === 'function') {
        window.initEntitiesAutocomplete();
    }
}

function getElementByExactId($scope: JQuery, id: string): JQuery {
    return $scope.find('[id]').filter(function matchExactId() {
        return this.id === id;
    }).first();
}

function getNextRowIndex(entityItemsTable: JQuery, rowsCount: number): number {
    let maxIndex = rowsCount - 1;

    entityItemsTable.find('tr.entityItemRow :input[name]').each(function eachEntityItemName() {
        const match = String($(this).attr('name') || '').match(/\[(\d+)\](?:\[\])?$/);
        if (match) {
            maxIndex = Math.max(maxIndex, Number(match[1]));
        }
    });

    return maxIndex + 1;
}

function setEntityItemsTableVisible(entityItemsTable: JQuery, visible: boolean): void {
    entityItemsTable.toggleClass('d-none disn', !visible);
}

function clearEntityItemsRequiredError(controlLabel: JQuery, controlErrors: JQuery): void {
    controlLabel.removeClass('text-danger');
    controlErrors.empty();
}

function showEntityItemsRequiredError(
    controlLabel: JQuery,
    controlErrors: JQuery,
    missingTranslate: string
): void {
    controlLabel.addClass('text-danger');
    const message = createErrorMsgElement(missingTranslate);
    controlErrors.empty().append(message);
    initLucideIcons(message);
}

/**
 * Dynamic rows for EntityItems form control (`entity_items.latte`).
 */
export function initEntityItems(name: string, rowsCount: number, missingTranslate: string): void {
    const $ = window.jQuery;
    const controlWrapper = $(`#items_${name}`);
    const controlLabel = $(`#items_${name} label`);
    const controlErrors = $(`#itemsControlErrors_${name}`);
    const entityItemsTable = $(`#entityItemsTable_${name}`);
    const templateRow = $(`#templateRow_${name}`);
    const entityItemRows = $(`#entityItemsTable_${name} .entityItemRow`);
    const eventNamespace = `.naisEntityItems.${name.replace(/[^A-Za-z0-9_-]/g, '')}`;
    let rowIndex = getNextRowIndex(entityItemsTable, rowsCount);

    if (controlWrapper.data('entityItemsInitialized')) {
        return;
    }

    controlWrapper.data('entityItemsInitialized', true);
    templateRow.find(':input').prop('disabled', true);

    if (entityItemRows.length > 0) {
        initRowWidgets(entityItemRows);
        setEntityItemsTableVisible(entityItemsTable, true);
    } else {
        setEntityItemsTableVisible(entityItemsTable, false);
    }

    $(`#addRow_${name}`).off(eventNamespace).on(`click${eventNamespace}`, () => {
        setEntityItemsTableVisible(entityItemsTable, true);
        const newIndex = rowIndex;
        const newRow = templateRow.clone();
        newRow.removeAttr('id');
        newRow.removeClass('hidden d-none');
        newRow.addClass('entityItemRow');
        newRow.find(':input').each(function reindexInput() {
            const input = $(this);
            const id = input.attr('id');
            const nameAttr = input.attr('name');

            if (nameAttr) {
                input.attr('name', nameAttr.replace('#index#', String(newIndex)));
            }

            input.prop('disabled', false);

            if (!id) {
                return;
            }

            const newId = `${id}_${newIndex}`;
            const newSpanId = `${newId}_message`;
            const span = getElementByExactId(newRow, `${id}_message`);

            input.attr('id', newId);
            input.attr('data-lfv-message-id', newSpanId);
            span.attr('id', newSpanId);
        });
        templateRow.before(newRow);
        initRowWidgets(newRow);
        window.initCalendars?.();
        rowIndex += 1;

        clearEntityItemsRequiredError(controlLabel, controlErrors);
    });

    entityItemsTable.off(eventNamespace, '.entityItemDelete').on(
        `click${eventNamespace}`,
        '.entityItemDelete',
        function onDelete(event) {
            event.preventDefault();
            $(this).closest('tr').remove();

            if ($(`#items_${name} .entityItemRow`).length === 0) {
                setEntityItemsTableVisible(entityItemsTable, false);
                if (controlWrapper.hasClass('required')) {
                    showEntityItemsRequiredError(controlLabel, controlErrors, missingTranslate);
                }
            }
        }
    );

    entityItemsTable.parents('form').off(eventNamespace).on(`submit${eventNamespace}`, () => {
        if (controlWrapper.hasClass('required') && $(`#items_${name} .entityItemRow`).length === 0) {
            showEntityItemsRequiredError(controlLabel, controlErrors, missingTranslate);
            return false;
        }

        clearEntityItemsRequiredError(controlLabel, controlErrors);
        return undefined;
    });
}

window.initEntityItems = initEntityItems;

export function initEntityItemsControls(root: ParentNode | Document = document): void {
    const $ = window.jQuery;
    const $root = root === document ? $(document) : $(root);
    const $controls = $root.is('[data-nais-entity-items]')
        ? $root
        : $root.find('[data-nais-entity-items]');

    $controls.each(function eachEntityItemsControl() {
        const $control = $(this);
        const name = String($control.attr('data-entity-items-name') || '');
        const rowsCount = Number($control.attr('data-entity-items-rows') || 0);
        const missingTranslate = String($control.attr('data-entity-items-missing') || '');

        if (name !== '') {
            initEntityItems(name, rowsCount, missingTranslate);
        }
    });
}

window.initEntityItemsControls = initEntityItemsControls;
